// Pure helpers for timetracker stats. No side effects; `now` is injected for testability.

function isoWeek(date) {
    // ISO 8601 week number.
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function isoWeekYear(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
}

export function sumHoursForPeriod(totalHours = [], period = 'month', now = new Date()) {
    const nowWeek = isoWeek(now);
    const nowWeekYear = isoWeekYear(now);
    const nowMonth = now.getFullYear() * 100 + now.getMonth();
    const nowYear = now.getFullYear();

    return (totalHours || []).reduce((sum, row) => {
        if (!row || !row.date) return sum;
        const [y, m, day] = row.date.split('-').map(n => parseInt(n, 10));
        const d = new Date(y, (m || 1) - 1, day || 1);
        const hours = Number(row.hours) || 0;
        if (period === 'week') {
            if (isoWeek(d) === nowWeek && isoWeekYear(d) === nowWeekYear) return sum + hours;
            return sum;
        }
        if (period === 'month') {
            if (d.getFullYear() * 100 + d.getMonth() === nowMonth) return sum + hours;
            return sum;
        }
        // year
        if (d.getFullYear() === nowYear) return sum + hours;
        return sum;
    }, 0);
}

export function runningTimerCount(timeTrackers = []) {
    return timeTrackers.filter(t => t && !t.endTime).length;
}

function isoDateLocal(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthsBack(now, months) {
    const y = now.getFullYear();
    const m = now.getMonth() - months;
    const lastDay = new Date(y, m + 1, 0).getDate();
    return new Date(y, m, Math.min(now.getDate(), lastDay));
}

export function presetRange(preset, now = new Date()) {
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (preset) {
        case 'thisMonth':
            return { startDate: isoDateLocal(new Date(y, m, 1)), endDate: isoDateLocal(new Date(y, m + 1, 0)) };
        case 'prevMonth':
            return { startDate: isoDateLocal(new Date(y, m - 1, 1)), endDate: isoDateLocal(new Date(y, m, 0)) };
        case 'last3Months':
            return { startDate: isoDateLocal(monthsBack(now, 3)), endDate: isoDateLocal(now) };
        case 'last12Months':
            return { startDate: isoDateLocal(monthsBack(now, 12)), endDate: isoDateLocal(now) };
        case 'thisYear':
            return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
        case 'all':
        default:
            return { startDate: '', endDate: '' };
    }
}

export function sumHours(totalHours = []) {
    return (totalHours || []).reduce((sum, row) => sum + (Number(row && row.hours) || 0), 0);
}

export function daysWorked(totalHours = []) {
    return (totalHours || []).filter(row => row && (Number(row.hours) || 0) > 0).length;
}

export function avgHoursPerWeek(totalHours = [], startDate = '', endDate = '') {
    const total = sumHours(totalHours);
    if (total === 0) {
        return 0;
    }
    let start = startDate ? new Date(`${startDate}T00:00:00`) : null;
    let end = endDate ? new Date(`${endDate}T00:00:00`) : null;
    if (!start || !end) {
        // No explicit bound ('Alt' or a one-sided custom range): derive from the data.
        const dates = (totalHours || []).map(r => r && r.date).filter(d => d).sort();
        if (dates.length === 0) {
            return 0;
        }
        if (!start) start = new Date(`${dates[0]}T00:00:00`);
        if (!end) end = new Date(`${dates[dates.length - 1]}T00:00:00`);
    }
    const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
    const weeks = Math.max(days / 7, 1);
    return total / weeks;
}

export function recentProjects(timeTrackers = [], runningTimers = []) {
    const projects = [];
    const seen = new Set();
    const keyOf = tt => `${tt.client || ''}::${tt.project || ''}::${tt.work || ''}`;
    const add = (tt) => {
        const key = keyOf(tt);
        if (seen.has(key)) return;
        seen.add(key);
        projects.push({
            key,
            client: tt.client || '',
            project: tt.project || '',
            work: tt.work || '',
            isRunning: false,
        });
    };
    const running = (runningTimers || []).filter(tt => tt && !tt.endTime);
    running.forEach(add);
    for (const tt of (timeTrackers || [])) {
        if (projects.length >= 8) break;
        if (tt) add(tt);
    }
    const runningKeys = new Set(running.map(keyOf));
    projects.forEach((p) => { p.isRunning = runningKeys.has(p.key); });
    projects.sort((a, b) => {
        if (a.isRunning && !b.isRunning) return -1;
        if (!a.isRunning && b.isRunning) return 1;
        return 0;
    });
    return projects;
}

const pad2 = n => String(n).padStart(2, '0');

// Elapsed time for a running timer, split for display. `now` is injected for
// testability. Never negative; a missing/invalid start gives all zeros.
export function elapsedSince(startTime, now = new Date()) {
    const start = startTime ? new Date(startTime).getTime() : NaN;
    const seconds = Number.isFinite(start) ? Math.max(0, Math.floor((now.getTime() - start) / 1000)) : 0;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return {
        seconds,
        hours,
        minutes,
        secs,
        hourFraction: (seconds % 3600) / 3600,
        hm: `${pad2(hours)}:${pad2(minutes)}`,
        ss: pad2(secs),
    };
}
